// Artifact panel component — sidebar listing artifacts and todos.
// Copied + factored from frontend/src/components/agent/ArtifactPanel.tsx.
// Factoring changes:
//   - Removed ArtifactViewer dependency; replaced with onOpenViewer callback prop.
//   - Removed ArtifactPreviewDialog, ArtifactTreeView dependencies; inline tree.
//   - Removed @ aliases; all imports local.
//   - onPinToDashboard is optional prop (Platinum-specific callback).
//   - Removed the list/tree view toggle; the panel always renders the tree.
// See ../../docs/90-provenance-map.md.
//
// Minimise behaviour (#1273): the panel is a <SlidePanel>, opened from the
// <EdgeReveal> pill on the chat's right edge and closed from the chevron in its
// own header. It no longer disappears when a session has nothing in it — an
// empty panel that someone deliberately opened says "Nothing here yet" rather
// than leaving them wondering whether the click registered.
//
// Agents (Chief of Staff, plan design/2026-10-26-chief-of-staff-agents-panel.md,
// T7): the first section lists the chat's sub-agents and workflows
// (AgentsSection). The panel still never opens by itself; a workflow card's
// click opens it through the host and passes `focusWorkflow`.
//
// A host's own section (`section`, AgentChat's panelSection) comes before
// everything: guided conversations draw their agenda tracker and notes there.

import React, { useCallback, useMemo } from 'react'
import { Box, Typography, IconButton, Tooltip } from '@mui/material'
import ArrowIcon from '@mui/icons-material/ArrowForward'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import PinIcon from '@mui/icons-material/PushPin'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import UncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import DotIcon from '@mui/icons-material/FiberManualRecord'
import SlidePanel from './SlidePanel.js'
import AgentsSection, { artifactAgentLabel } from './AgentsSection.js'
import { useDelegatedAgents } from './DelegatedAgentsContext.js'
import type { DelegatedEntry } from '../delegatedAgents.js'
import { ArtifactGlyph } from './artifactGlyph.js'
import type { ArtifactInfo, TodoItem } from '../types.js'

/** Open width of the right-hand panel, in px. */
export const ARTIFACT_PANEL_WIDTH = 320

interface ArtifactPanelProps {
  /**
   * Open state, owned by the host page. Defaults to open so a host that has not
   * adopted the minimise controls keeps the panel it always had.
   */
  open?: boolean
  /** Called from the header chevron. Closes this panel only. */
  onClose?: () => void
  artifacts: ArtifactInfo[]
  todos?: TodoItem[]
  sessionId: string
  customer?: string
  /** Called when user clicks pin-to-dashboard (Platinum-specific). */
  onPinToDashboard?: (sessionId: string) => void
  /** Called when user clicks "View all artifacts". */
  onViewAll?: () => void
  /** Called when an artifact is clicked for preview. */
  onArtifactClick?: (artifact: ArtifactInfo) => void
  /** API base URL for download links. Default: empty string. */
  apiBaseUrl?: string
  /** Auth header for downloads. */
  authHeader?: string
  /** The chat's sub-agents and workflows (Chief of Staff), drawn first. */
  agents?: DelegatedEntry[]
  /** Scroll to and open this workflow on every new nonce. */
  focusWorkflow?: { toolCallId: string; nonce: number } | null
  /** The host's own section (AgentChat's panelSection), drawn first. */
  section?: React.ReactNode
}

const STATUS_DOT_COLORS: Record<string, string> = {
  live: '#16a34a',
  extracted: '#2563eb',
  lost: '#9ca3af',
}

function TodoStatusIcon({ status }: { status: TodoItem['status'] }) {
  if (status === 'completed') return <CheckCircleIcon sx={{ fontSize: 16, color: '#16a34a' }} />
  if (status === 'in_progress') return <DotIcon sx={{ fontSize: 16, color: '#2563eb', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } }, animation: 'pulse 1.5s ease-in-out infinite' }} />
  return <UncheckedIcon sx={{ fontSize: 16, color: '#9ca3af' }} />
}

// Simple flat list tree view (no external ArtifactTreeView dependency)
function ArtifactFlatTree({ artifacts, onSelect, agentLabel }: { artifacts: ArtifactInfo[]; onSelect: (a: ArtifactInfo) => void; agentLabel?: (a: ArtifactInfo) => string | undefined }) {
  // Group by directory
  const grouped = useMemo(() => {
    const map = new Map<string, ArtifactInfo[]>()
    // A directory artifact (e.g. a captured webapp folder) is redundant with
    // the "<dir>/" group header its own children already render under —
    // showing both reads as the same folder listed twice. Skip a dir artifact
    // whenever some other artifact's path actually falls under it.
    const dirPath = (a: ArtifactInfo) => a.filePath.replace(/\/$/, '')
    const hasChildUnder = (path: string) =>
      artifacts.some(other => other.filePath !== path && other.filePath.startsWith(`${path}/`))
    for (const a of artifacts) {
      if (a.isDir && hasChildUnder(dirPath(a))) continue
      const parts = a.filePath.split('/')
      const dir = parts.length > 1 ? parts.slice(0, -1).join('/') : '/'
      const existing = map.get(dir) || []
      existing.push(a)
      map.set(dir, existing)
    }
    return map
  }, [artifacts])

  return (
    <Box>
      {Array.from(grouped.entries()).map(([dir, items]) => (
        <Box key={dir}>
          {dir !== '/' && (
            <Typography sx={{ fontSize: 11, color: '#9ca3af', px: 1, py: 0.25, fontFamily: 'monospace' }}>
              {dir}/
            </Typography>
          )}
          {items.map(a => (
            <Box
              key={a.filePath}
              data-testid="artifact-entry"
              data-lost={a.status === 'lost' ? 'true' : undefined}
              onClick={() => onSelect(a)}
              sx={{
                px: dir !== '/' ? 2 : 1,
                py: 0.5,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                opacity: a.status === 'lost' ? 0.6 : 1,
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: STATUS_DOT_COLORS[a.status] || '#9ca3af', flexShrink: 0 }} />
              <ArtifactGlyph artifact={a} size={14} />
              <Typography sx={{ fontSize: 12, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: a.status === 'lost' ? 'line-through' : 'none' }}>
                {a.fileName}
              </Typography>
              {agentLabel?.(a) && (
                <Typography data-testid="artifact-agent" sx={{ fontSize: 11, color: 'text.secondary', flexShrink: 0, ml: 'auto', pl: 0.5, maxWidth: '45%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {agentLabel(a)}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  )
}

export default function ArtifactPanel({
  open = true,
  onClose,
  artifacts,
  todos,
  sessionId,
  onPinToDashboard,
  onViewAll,
  onArtifactClick,
  agents,
  focusWorkflow = null,
  section,
}: ArtifactPanelProps) {
  const handleArtifactClick = useCallback((artifact: ArtifactInfo) => {
    if (onArtifactClick) onArtifactClick(artifact)
  }, [onArtifactClick])

  const handleViewAll = useCallback(() => {
    if (onViewAll) onViewAll()
  }, [onViewAll])

  // Which agent made a file (Chief of Staff): named beside it in the list.
  const runs = useDelegatedAgents()?.runs
  const agentLabel = useCallback((a: ArtifactInfo) => artifactAgentLabel(a, agents, runs), [agents, runs])

  const hasTodos = todos && todos.length > 0
  const hasAgents = !!agents && agents.length > 0
  const hasSection = !!section
  const isEmpty = artifacts.length === 0 && !hasTodos && !hasAgents && !hasSection
  // Alone, the host's section takes the panel; beside the lists it is capped
  // and scrolls, as the Agents section does.
  const sectionAlone = artifacts.length === 0 && !hasTodos && !hasAgents
  const completedCount = hasTodos ? todos.filter(t => t.status === 'completed').length : 0

  return (
    <SlidePanel open={open} width={ARTIFACT_PANEL_WIDTH} side="right">
      <Box
        sx={{
          flex: 1,
          borderLeft: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', minHeight: 36, borderBottom: '1px solid #e5e7eb' }}>
          <Tooltip title="Close panel">
            <IconButton size="small" aria-label="Close panel" onClick={onClose} sx={{ mr: 0.5, color: '#6b7280' }}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {isEmpty && (
          <Typography sx={{ p: '12px', fontSize: 12, color: '#9ca3af' }}>Nothing here yet</Typography>
        )}
        {hasSection && (
          <Box data-testid="artifact-panel-section" sx={sectionAlone ? { flex: 1, minHeight: 0, overflow: 'auto' } : { flexShrink: 0, maxHeight: '55%', overflow: 'auto' }}>
            {section}
          </Box>
        )}
        {hasAgents && (
          // Its own scroll, capped, so a long workflow cannot push Tasks and
          // Artifacts off the panel.
          <Box sx={{ flexShrink: 0, maxHeight: '45%', overflow: 'auto' }}>
            <AgentsSection agents={agents} focusWorkflow={focusWorkflow} />
          </Box>
        )}
        {hasTodos && (
          <Box sx={{ p: '8px 12px', borderBottom: '1px solid #e5e7eb' }}>
            <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 0.75 }}>
              Tasks ({completedCount}/{todos.length})
            </Typography>
            {todos.map((todo, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.5 }}>
                <TodoStatusIcon status={todo.status} />
                <Typography sx={{
                  fontSize: 12,
                  color: todo.status === 'completed' ? '#9ca3af' : '#374151',
                  textDecoration: todo.status === 'completed' ? 'line-through' : 'none',
                  lineHeight: 1.4,
                }}>
                  {todo.content}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        {/* As before the Agents section: with only agents, no empty Artifacts list. */}
        {(artifacts.length > 0 || hasTodos) && (
          <>
            <Box sx={{ p: '8px 12px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 14, flex: 1 }}>
                Artifacts ({artifacts.length})
              </Typography>
              {onPinToDashboard && (
                <IconButton
                  size="small"
                  onClick={() => onPinToDashboard(sessionId)}
                  title="Pin to dashboard"
                  aria-label="pin to dashboard"
                  sx={{ color: '#6b7280' }}
                >
                  <PinIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto', p: 0.5 }}>
              <ArtifactFlatTree artifacts={artifacts} onSelect={handleArtifactClick} agentLabel={agentLabel} />
            </Box>
            <Box
              onClick={handleViewAll}
              sx={{
                p: '8px 16px',
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#f1f5f9' },
              }}
            >
              <Typography sx={{ fontSize: 12, color: '#2563eb', fontWeight: 500 }}>View all artifacts</Typography>
              <ArrowIcon sx={{ fontSize: 14, color: '#2563eb', ml: 0.5 }} />
            </Box>
          </>
        )}
      </Box>
    </SlidePanel>
  )
}
